import React, { useEffect, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import {
  dynamicIvrDtmfOptionsInitialValue,
  forwardingTypeOptions,
  maxRetriesOptions,
  queueDropTimeOptions
} from '../../../utils/common';
import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormTextarea from '../../../components/form/FormTextarea';
import FormToggle from '../../../components/form/FormToggle';

import {
  audioPromptsAllOptionGet,
  didProcessOptionGet,
  didUserOptionGet,
  dynamicIvrAdd,
  dynamicIvrUpdate,
  dynamicIvrDetailsGetById,
  ivrOptionsGet,
  queuesOptionGet
} from '../../../store/slices/tenantSlices/incomingSlice';
import { crmsOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import {
  callexAiAgentOptionsGet,
  crmsDetailsGetById
} from '../../../store/slices/tenantSlices/configurationSlice';

const extractPlaceholderKeys = (script) => {
  const matches = String(script || '').match(/\{\{\s*([^{}]+?)\s*\}\}/g) || [];
  const keys = matches.map((m) => m.replace(/[{}]/g, '').trim());
  return [...new Set(keys)].filter(Boolean);
};

const AddEditDynamicIvrModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();

  const [crmOptions, setCrmOptions] = useState([]);
  const [crmFieldOptions, setCrmFieldOptions] = useState([]);
  const [mappingRows, setMappingRows] = useState([]);
  const [mappingErrors, setMappingErrors] = useState([]);

  const [userOptions, setUserOptions] = useState([]);
  const [queuesOption, setQueuesOption] = useState([]);
  const [ivrOption, setIvrOption] = useState([]);
  const [processOptions, setProcessOptions] = useState([]);
  const [audioPromptOption, setAudioPromptOption] = useState([]);
  const [aiPhoneNumberOption, setAiPhoneNumberOption] = useState([]);
  const [dtmfOptions, setDtmfOptions] = useState([...dynamicIvrDtmfOptionsInitialValue]);

  const initialValues = {
    ivr_name: '',
    script: '',
    crm_id: '',
    invalid_prompt: '',
    exit_prompt: '',
    max_retries: '',
    wait_duration: '',
    status: 'true'
  };

  const validationSchema = Yup.object({
    ivr_name: Yup.string().required('ivr name is required'),
    script: Yup.string().required('script is required'),
    crm_id: Yup.string().required('crm is required')
  });

  const buildMappingFields = () => {
    const mapping = {};
    mappingRows.forEach((row) => {
      const key = String(row.key).trim();
      const value = String(row.value).trim();
      if (key && value) {
        mapping[key] = value;
      }
    });
    return mapping;
  };

  const validateMappingRows = () => {
    const rowErrors = mappingRows.map((row) => ({
      value: String(row.value).trim() ? '' : 'Mapping field is required'
    }));
    setMappingErrors(rowErrors);
    return rowErrors.every((err) => !err.value);
  };

  const resetDtmfOptions = () => setDtmfOptions([...dynamicIvrDtmfOptionsInitialValue]);

  const onSubmit = async () => {
    if (!validateMappingRows()) {
      return;
    }
    const mapping_fields = buildMappingFields();
    const filteredDtmfOptions = dtmfOptions.filter(
      (option) => option.forwarding_type && option.forwarding_option
    );

    if (isEdit) {
      try {
        const payload = {
          ...values,
          id: editRecord.id,
          mapping_fields,
          dtmf_options: filteredDtmfOptions
        };
        const response = await dispatch(dynamicIvrUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          setMappingRows([]);
          setMappingErrors([]);
          resetDtmfOptions();
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values, mapping_fields, dtmf_options: filteredDtmfOptions };
        const response = await dispatch(dynamicIvrAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          setMappingRows([]);
          resetDtmfOptions();
          onCloseClick(true);
          resetForm();
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchDynamicIvrDetailsGetById = async () => {
    try {
      const response = await dispatch(dynamicIvrDetailsGetById(editRecord?.id)).unwrap();
      const record = response?.data || response?.ivr_dynamic || response;
      setValues({
        ivr_name: String(record?.ivr_name || ''),
        script: String(record?.script || ''),
        crm_id: String(record?.crm_id || ''),
        invalid_prompt: record?.invalid_prompt ? String(record.invalid_prompt) : '',
        exit_prompt: record?.exit_prompt ? String(record.exit_prompt) : '',
        max_retries: record?.max_retries ? String(record.max_retries) : '',
        wait_duration: record?.wait_duration ? Number(record.wait_duration) : '',
        status: String(record?.status === undefined ? true : record.status)
      });

      if (record?.crm_id) {
        getCrmFields(record.crm_id);
      }

      const savedMapping = record?.mapping_fields || {};
      const rows = extractPlaceholderKeys(record?.script).map((key) => ({
        key,
        value: savedMapping[key] ? String(savedMapping[key]) : ''
      }));
      setMappingRows(rows);

      const updatedDtmfOptions = [...dynamicIvrDtmfOptionsInitialValue];
      (response?.dtmf_options || []).forEach((option) => {
        const index = updatedDtmfOptions.findIndex(
          (dtmfOption) => dtmfOption.dtmf === Number(option.dtmf)
        );
        if (index !== -1) {
          updatedDtmfOptions[index] = {
            ...updatedDtmfOptions[index],
            forwarding_type: option.forwarding_type || '',
            forwarding_option: option.forwarding_option || ''
          };
        }
      });
      setDtmfOptions(updatedDtmfOptions);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit && editRecord) {
      fetchDynamicIvrDetailsGetById();
    }
  }, [editRecord]);

  const getCrmsOption = async () => {
    try {
      const response = await dispatch(crmsOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setCrmOptions(options);
      } else {
        setCrmOptions([]);
      }
    } catch (error) {
      setCrmOptions([]);
      console.log('error', error);
    }
  };

  const getCrmFields = async (crmId) => {
    try {
      const response = await dispatch(crmsDetailsGetById(crmId)).unwrap();
      const fields = response?.fields || response?.data?.fields || [];
      const options = fields.map((field) => ({
        label: field.label,
        value: field.field_rank
      }));
      setCrmFieldOptions(options);
    } catch (error) {
      setCrmFieldOptions([]);
      console.log('error', error);
    }
  };

  const getAudioPromptOption = async () => {
    try {
      const response = await dispatch(audioPromptsAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setAudioPromptOption(options);
      } else {
        setAudioPromptOption([]);
      }
    } catch (error) {
      setAudioPromptOption([]);
      console.log('error', error);
    }
  };

  const getIvrOption = async () => {
    try {
      const response = await dispatch(ivrOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setIvrOption(options);
      } else {
        setIvrOption([]);
      }
    } catch (error) {
      setIvrOption([]);
      console.log('error', error);
    }
  };

  const getQueuesOption = async () => {
    try {
      const response = await dispatch(queuesOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setQueuesOption(options);
      } else {
        setQueuesOption([]);
      }
    } catch (error) {
      setQueuesOption([]);
      console.log('error', error);
    }
  };

  const getProcessOption = async () => {
    try {
      const response = await dispatch(didProcessOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  const getUserOption = async () => {
    try {
      const response = await dispatch(didUserOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: String(val.id)
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      setUserOptions([]);
      console.log('error', error);
    }
  };

  const getAIAgentOption = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id) || ''
        }));
        setAiPhoneNumberOption(options);
      } else {
        setAiPhoneNumberOption([]);
      }
    } catch (error) {
      setAiPhoneNumberOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getCrmsOption();
    getAudioPromptOption();
    getIvrOption();
    getQueuesOption();
    getProcessOption();
    getUserOption();
    getAIAgentOption();
  }, []);

  useEffect(() => {
    const keys = extractPlaceholderKeys(values.script);
    setMappingRows((prev) =>
      keys.map((key) => {
        const existing = prev.find((row) => row.key === key);
        return { key, value: existing?.value || '' };
      })
    );
    setMappingErrors([]);
  }, [values.script]);

  const handleMappingChange = (index, event) => {
    const { name, value } = event.target;
    const updated = [...mappingRows];
    updated[index] = { ...updated[index], [name]: value };
    setMappingRows(updated);
    if (mappingErrors[index]?.[name]) {
      const updatedErrors = [...mappingErrors];
      updatedErrors[index] = { ...updatedErrors[index], [name]: '' };
      setMappingErrors(updatedErrors);
    }
  };

  const getForwardingOptions = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_process') {
      return processOptions;
    }
    if (forwarding_type === 'transfer_to_queue') {
      return queuesOption;
    }
    if (forwarding_type === 'transfer_to_playback') {
      return audioPromptOption;
    }
    if (forwarding_type === 'transfer_to_agent') {
      return userOptions;
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return ivrOption;
    }
    if (forwarding_type === 'transfer_to_ai_number') {
      return aiPhoneNumberOption;
    }
    return [];
  };

  const getForwardingLabel = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_process') {
      return 'Process';
    }
    if (forwarding_type === 'transfer_to_queue') {
      return 'Queue';
    }
    if (forwarding_type === 'transfer_to_playback') {
      return 'Playback';
    }
    if (forwarding_type === 'transfer_to_agent') {
      return 'User';
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return 'IVR';
    }
    return '';
  };

  const isAgentForwarding = (forwarding_type) => forwarding_type === 'transfer_to_agent';

  const getForwardingSelectValue = (forwarding_type, value) =>
    isAgentForwarding(forwarding_type)
      ? value
        ? String(value).split(',').filter(Boolean)
        : []
      : value;

  const wrapForwardingOnChange = (forwarding_type, name, onChange) =>
    isAgentForwarding(forwarding_type)
      ? (e) =>
          onChange({
            target: {
              name,
              value: Array.isArray(e.target.value) ? e.target.value.join(',') : e.target.value
            }
          })
      : onChange;

  const handleDtmfChange = (index, event) => {
    const { name, value } = event.target;
    const updatedDtmf = [...dtmfOptions];
    updatedDtmf[index] = { ...updatedDtmf[index], [name]: value };
    if (name === 'forwarding_type') {
      updatedDtmf[index] = {
        ...updatedDtmf[index],
        forwarding_option: value === 'transfer_to_hangup' ? 'Hangup' : ''
      };
    }
    setDtmfOptions(updatedDtmf);
  };

  const forwardingSelectOptions = [
    'transfer_to_process',
    'transfer_to_queue',
    'transfer_to_playback',
    'transfer_to_agent',
    'transfer_to_ivr',
    'transfer_to_ai_number'
  ];

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Dynamic IVR' : 'Add Dynamic IVR'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="dynamic-ivr" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="ivr_name"
                label="IVR Name"
                placeholder="Enter IVR name"
                value={values.ivr_name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="crm_id"
                label="CRM"
                placeholder="Select CRM"
                value={values.crm_id}
                options={crmOptions}
                onChange={(e) => {
                  handleChange(e);
                  setCrmFieldOptions([]);
                  setMappingRows((prev) => prev.map((row) => ({ ...row, value: '' })));
                  setMappingErrors([]);
                  if (e.target.value) {
                    getCrmFields(e.target.value);
                  }
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="wait_duration"
                label="Wait Duration"
                placeholder="Select Wait Duration"
                value={values.wait_duration}
                options={queueDropTimeOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="max_retries"
                label="Max Retries"
                placeholder="Select Max Retries"
                value={values.max_retries}
                options={maxRetriesOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="invalid_prompt"
                label="Invalid Prompt"
                placeholder="Select Invalid Prompt"
                value={values.invalid_prompt}
                options={audioPromptOption}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="exit_prompt"
                label="Exit Prompt"
                placeholder="Select Exit Prompt"
                value={values.exit_prompt}
                options={audioPromptOption}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="status"
                label="Status"
                value={values.status}
                onChange={(e) => setFieldValue('status', e)}
              />
            </Col>
            <Col lg="12">
              <FormTextarea
                name="script"
                label="Script"
                placeholder="hello {{1}} welcome to callex"
                value={values.script}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
          <div className="w-100 py-2 px-3 bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
            <div>Mapping Fields</div>
          </div>
          {mappingRows.length > 0 ? (
            <>
              <Row className="pt-3 pb-2">
                <Col lg="6" className="fw-bold text-dark fs-6">
                  Placeholder Key
                </Col>
                <Col lg="6" className="fw-bold text-dark fs-6">
                  Mapping Field
                </Col>
              </Row>
              {mappingRows.map((row, index) => {
                return (
                  <Row key={row.key} className="align-items-center py-2">
                    <Col lg="6">
                      <FormInput
                        className="callex-mb-0 mb-1"
                        isShowLabel={false}
                        name="key"
                        value={`{{${row.key}}}`}
                        disabled
                        onChange={() => {}}
                      />
                    </Col>
                    <Col lg="6">
                      <FormSelect
                        className="callex-mb-0 mb-1"
                        isShowLabel={false}
                        name="value"
                        placeholder="Select Mapping Field"
                        value={row.value}
                        options={crmFieldOptions}
                        onChange={(e) => handleMappingChange(index, e)}
                      />
                      {mappingErrors[index]?.value && (
                        <span className="text-danger fs-6">{mappingErrors[index].value}</span>
                      )}
                    </Col>
                  </Row>
                );
              })}
            </>
          ) : (
            <div className="text-center text-muted py-3">
              Add a placeholder like {'{{1}}'} in the script to map fields.
            </div>
          )}
          <div className="w-100 mt-3 py-2 px-3 bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
            <div>DTMF Options</div>
          </div>
          <Row className="pt-4 pb-2">
            <Col lg="1" md="2" className="text-center fw-bold text-primary fs-6">
              DTMF
            </Col>
            <Col lg="4" md="4" className="text-center fw-bold text-primary fs-6">
              Action
            </Col>
            <Col lg="4" md="4" className="text-center fw-bold text-primary fs-6">
              Parameter
            </Col>
          </Row>
          {dtmfOptions?.map((val, index) => {
            return (
              <Row key={val.dtmf} className="py-2">
                <Col lg="1" md="2" className="text-center text-primary fs-6">
                  {val?.dtmf}
                </Col>
                <Col lg="4" md="4">
                  <FormSelect
                    className="callex-mb-0"
                    isShowLabel={false}
                    name="forwarding_type"
                    placeholder="Select Forwarding Type"
                    value={val.forwarding_type}
                    options={forwardingTypeOptions}
                    onChange={(e) => handleDtmfChange(index, e)}
                  />
                </Col>

                {val.forwarding_type !== '' ? (
                  <Col lg="4" md="4">
                    {forwardingSelectOptions.includes(val.forwarding_type) ? (
                      <FormSelect
                        className="callex-mb-0"
                        isShowLabel={false}
                        name="forwarding_option"
                        placeholder={`Select ${getForwardingLabel(val.forwarding_type)}`}
                        value={getForwardingSelectValue(val.forwarding_type, val.forwarding_option)}
                        options={getForwardingOptions(val.forwarding_type)}
                        isMulti={isAgentForwarding(val.forwarding_type)}
                        onChange={wrapForwardingOnChange(
                          val.forwarding_type,
                          'forwarding_option',
                          (e) => handleDtmfChange(index, e)
                        )}
                      />
                    ) : val.forwarding_type === 'transfer_to_call_forward' ? (
                      <FormInput
                        className="callex-mb-0"
                        isShowLabel={false}
                        name="forwarding_option"
                        label="Call Forward (PSTN)"
                        placeholder="Enter Call Forward"
                        value={val.forwarding_option}
                        onChange={(e) => handleDtmfChange(index, e)}
                      />
                    ) : val.forwarding_type === 'transfer_to_hangup' ? (
                      <FormInput
                        className="callex-mb-0"
                        isShowLabel={false}
                        name="forwarding_option"
                        label="Hangup"
                        placeholder="Enter Hangup"
                        value={val.forwarding_option}
                        onChange={(e) => handleDtmfChange(index, e)}
                        disabled={true}
                      />
                    ) : null}
                  </Col>
                ) : null}
              </Row>
            );
          })}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setMappingRows([]);
            setMappingErrors([]);
            resetDtmfOptions();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="dynamic-ivr" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditDynamicIvrModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditDynamicIvrModal;
